// Mobile port of the web Journal Configuration (Settings → Journal
// Configuration → Entry Types). Reads/writes `user_settings.
// custom_journal_entry_types` directly via useSettingColumn — same source of
// truth as web's CustomJournalEntryType (src/interfaces/settings.ts).
// Guiding-principles, font, and goal-tree sub-features are intentionally left
// out (too deep for a first mobile pass) — their fields are simply absent.
import React, { useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';
import { SectionTitle, Chip, Field, GoldButton, GlassCard } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

interface JournalPrompt {
  id: string;
  text: string;
  type: 'text' | 'dropdown';
  options?: string[];
}

interface WordCountSettings {
  showWordCount: boolean;
  useWordCountTarget: boolean;
  wordCountTarget: number;
}

interface CustomJournalEntryType {
  id: string;
  title: string;
  prompts: JournalPrompt[];
  wordCountSettings?: WordCountSettings;
  showTKDT?: boolean;
  showItinerary?: boolean;
  showGoals?: boolean;
  additionalEntryEnabled?: boolean;
}

const FALLBACK: CustomJournalEntryType[] = [];

export default function JournalTypesEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value, setValue, loading } = useSettingColumn<CustomJournalEntryType[]>(
    userId,
    'custom_journal_entry_types',
    FALLBACK
  );
  const [editingId, setEditingId] = useState<string | null>(null);

  if (loading) {
    return <Text style={styles.hint}>Loading…</Text>;
  }

  const editing = value.find((t) => t.id === editingId) ?? null;

  const updateType = (id: string, next: Partial<CustomJournalEntryType>) => {
    setValue(value.map((t) => (t.id === id ? { ...t, ...next } : t)));
  };

  const addType = () => {
    const id = `cj-type-${Date.now()}`;
    const next: CustomJournalEntryType = { id, title: 'New Type', prompts: [] };
    setValue([...value, next]);
    setEditingId(id);
  };

  const deleteType = (id: string) => {
    Alert.alert('Delete entry type?', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          setValue(value.filter((t) => t.id !== id));
          if (editingId === id) setEditingId(null);
        },
      },
    ]);
  };

  if (editing) {
    return <TypeEditor type={editing} onChange={(next) => updateType(editing.id, next)} onDone={() => setEditingId(null)} />;
  }

  return (
    <View>
      <SectionTitle style={styles.sectionGap}>Journal Entry Types</SectionTitle>
      <Text style={styles.hint}>Customize what shows up when journaling — prompts, word count, sections.</Text>

      {value.length === 0 ? (
        <Text style={styles.hint}>No custom entry types yet.</Text>
      ) : (
        value.map((t) => (
          <View key={t.id} style={styles.typeRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.typeTitle}>{t.title || 'Untitled'}</Text>
              <Text style={styles.typeMeta}>
                {t.prompts.length} prompt{t.prompts.length === 1 ? '' : 's'}
              </Text>
            </View>
            <Pressable style={styles.smallButton} onPress={() => setEditingId(t.id)}>
              <Text style={styles.smallButtonText}>Edit</Text>
            </Pressable>
            <Pressable style={styles.smallDangerButton} onPress={() => deleteType(t.id)}>
              <Text style={styles.smallDangerText}>Delete</Text>
            </Pressable>
          </View>
        ))
      )}

      <GoldButton label="Add New Entry Type" onPress={addType} style={styles.actionGap} />
    </View>
  );
}

function TypeEditor({
  type,
  onChange,
  onDone,
}: {
  type: CustomJournalEntryType;
  onChange: (next: Partial<CustomJournalEntryType>) => void;
  onDone: () => void;
}) {
  const styles = useStyles();
  const wc: WordCountSettings = type.wordCountSettings ?? {
    showWordCount: false,
    useWordCountTarget: false,
    wordCountTarget: 250,
  };

  const setPrompts = (prompts: JournalPrompt[]) => onChange({ prompts });

  const addPrompt = (kind: 'text' | 'dropdown') => {
    const prompt: JournalPrompt = {
      id: `cj-prompt-${Date.now()}`,
      text: '',
      type: kind,
      options: kind === 'dropdown' ? [] : undefined,
    };
    setPrompts([...type.prompts, prompt]);
  };

  const updatePrompt = (id: string, next: Partial<JournalPrompt>) => {
    setPrompts(type.prompts.map((p) => (p.id === id ? { ...p, ...next } : p)));
  };

  const deletePrompt = (id: string) => {
    setPrompts(type.prompts.filter((p) => p.id !== id));
  };

  const movePrompt = (id: string, direction: -1 | 1) => {
    const idx = type.prompts.findIndex((p) => p.id === id);
    const swapIdx = idx + direction;
    if (idx === -1 || swapIdx < 0 || swapIdx >= type.prompts.length) return;
    const next = [...type.prompts];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setPrompts(next);
  };

  const setWordCount = (next: Partial<WordCountSettings>) => {
    onChange({ wordCountSettings: { ...wc, ...next } });
  };

  return (
    <View>
      <View style={styles.editorHeader}>
        <SectionTitle style={styles.sectionGap}>Edit Entry Type</SectionTitle>
        <Pressable style={styles.smallButton} onPress={onDone}>
          <Text style={styles.smallButtonText}>Done</Text>
        </Pressable>
      </View>

      <Text style={styles.label}>Title</Text>
      <Field inputStyle={styles.input} value={type.title} onChangeText={(t) => onChange({ title: t })} placeholder="Entry type title" />

      <SectionTitle style={styles.sectionGap}>Prompts</SectionTitle>
      {type.prompts.length === 0 ? (
        <Text style={styles.hint}>No prompts yet.</Text>
      ) : (
        type.prompts.map((p, idx) => (
          <GlassCard key={p.id} style={styles.promptCard}>
            <View style={styles.promptHeaderRow}>
              <View style={styles.reorderCol}>
                <Pressable
                  onPress={() => movePrompt(p.id, -1)}
                  disabled={idx === 0}
                  style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
                >
                  <Text style={styles.reorderText}>▲</Text>
                </Pressable>
                <Pressable
                  onPress={() => movePrompt(p.id, 1)}
                  disabled={idx === type.prompts.length - 1}
                  style={[styles.reorderBtn, idx === type.prompts.length - 1 && styles.reorderBtnDisabled]}
                >
                  <Text style={styles.reorderText}>▼</Text>
                </Pressable>
              </View>
              <Field style={styles.flex1}
                inputStyle={[styles.input, { flex: 1 }]}
                value={p.text}
                onChangeText={(t) => updatePrompt(p.id, { text: t })}
                placeholder="Prompt text"
              />
            </View>

            <View style={styles.toggleRow}>
              <Chip label="Text" active={p.type === 'text'} onPress={() => updatePrompt(p.id, { type: 'text', options: undefined })} />
              <Chip label="Dropdown" active={p.type === 'dropdown'} onPress={() => updatePrompt(p.id, { type: 'dropdown', options: p.options ?? [] })} />
              <Pressable style={styles.smallDangerButton} onPress={() => deletePrompt(p.id)}>
                <Text style={styles.smallDangerText}>Delete</Text>
              </Pressable>
            </View>

            {p.type === 'dropdown' ? (
              <View style={styles.optionsBox}>
                <Text style={styles.label}>Options</Text>
                {(p.options ?? []).map((opt, optIdx) => (
                  <View key={optIdx} style={styles.optionRow}>
                    <Field style={styles.flex1}
                      inputStyle={[styles.input, { flex: 1 }]}
                      value={opt}
                      onChangeText={(t) => {
                        const next = [...(p.options ?? [])];
                        next[optIdx] = t;
                        updatePrompt(p.id, { options: next });
                      }}
                      placeholder={`Option ${optIdx + 1}`}
                    />
                    <Pressable
                      style={styles.smallDangerButton}
                      onPress={() => {
                        const next = (p.options ?? []).filter((_, i) => i !== optIdx);
                        updatePrompt(p.id, { options: next });
                      }}
                    >
                      <Text style={styles.smallDangerText}>✕</Text>
                    </Pressable>
                  </View>
                ))}
                <GoldButton label="Add Option" onPress={() => updatePrompt(p.id, { options: [...(p.options ?? []), ''] })} style={styles.actionGap} />
              </View>
            ) : null}
          </GlassCard>
        ))
      )}

      <View style={styles.toggleRow}>
        <GoldButton label="Add Text Prompt" onPress={() => addPrompt('text')} style={styles.actionGap} />
        <GoldButton label="Add Dropdown Prompt" onPress={() => addPrompt('dropdown')} style={styles.actionGap} />
      </View>

      <SectionTitle style={styles.sectionGap}>Sections</SectionTitle>
      <SwitchRow label="Show TKDT" value={!!type.showTKDT} onValueChange={(v) => onChange({ showTKDT: v })} />
      <SwitchRow label="Show Itinerary" value={!!type.showItinerary} onValueChange={(v) => onChange({ showItinerary: v })} />
      <SwitchRow
        label="Enable Additional Entries"
        value={!!type.additionalEntryEnabled}
        onValueChange={(v) => onChange({ additionalEntryEnabled: v })}
      />
      <SwitchRow label="Show Goals" value={!!type.showGoals} onValueChange={(v) => onChange({ showGoals: v })} />

      <SectionTitle style={styles.sectionGap}>Word Count</SectionTitle>
      <SwitchRow label="Show Word Count" value={wc.showWordCount} onValueChange={(v) => setWordCount({ showWordCount: v })} />
      <SwitchRow
        label="Use Word Count Target"
        value={wc.useWordCountTarget}
        onValueChange={(v) => setWordCount({ useWordCountTarget: v })}
      />
      {wc.useWordCountTarget ? (
        <View style={{ marginTop: 8 }}>
          <Text style={styles.label}>Target Word Count</Text>
          <Field
            inputStyle={styles.input}
            value={String(wc.wordCountTarget)}
            onChangeText={(t) => {
              const n = parseInt(t.replace(/[^0-9]/g, ''), 10);
              setWordCount({ wordCountTarget: Number.isNaN(n) ? 0 : n });
            }}
            keyboardType="number-pad"
            placeholder="250"
          />
        </View>
      ) : null}

      <GoldButton ghost label="Done" onPress={onDone} style={[styles.actionGap, { marginTop: 20 }]} />
    </View>
  );
}

function SwitchRow({ label, value, onValueChange }: { label: string; value: boolean; onValueChange: (v: boolean) => void }) {
  const styles = useStyles();
  return (
    <View style={styles.switchRow}>
      <Text style={styles.switchLabel}>{label}</Text>
      <ThemedSwitch value={value} onValueChange={onValueChange} />
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  flex1: { flex: 1 },
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  label: { fontSize: 12, fontWeight: '700', color: t.ivory, marginTop: 8, marginBottom: 4 },
  input: {
  },

  typeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  typeTitle: { fontSize: 14, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  typeMeta: { fontSize: 12, color: t.ivorySoft, marginTop: 2 },

  smallButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  smallButtonText: { fontSize: 12, fontWeight: '800', color: t.goldBright },
  smallDangerButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  smallDangerText: { fontSize: 12, fontWeight: '800', color: t.lapsed },


  editorHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },

  promptCard: {
    padding: 10,
    marginTop: 10,
  },
  promptHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  reorderCol: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },

  toggleRow: { flexDirection: 'row', gap: 8, marginTop: 10, alignItems: 'center' },

  optionsBox: { marginTop: 10 },
  optionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 },

  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  switchLabel: { fontSize: 13, fontWeight: '700', color: t.ivory },
}),
);
